/**
 * PwaInstallChip — v146.38
 *
 * Floating "Add to Home Screen" pill for public bio hubs (/u/{slug}).
 * The installed app opens `/u/{slug}?source=pwa` chromeless, showing
 * the creator's name + icon on the visitor's home screen.
 *
 * Behaviour:
 *   • Injects <link rel="manifest"> for /api/pwa/manifest/{slug}.webmanifest
 *     into <head> for the duration this component is mounted, plus a
 *     matching <meta name="theme-color"> so the standalone chrome
 *     matches the hub's palette.
 *   • Registers /pwa-bio-sw.js — required for Chrome's install prompt.
 *   • Listens for `beforeinstallprompt`; on Android/desktop Chrome we
 *     call `event.prompt()` when the visitor taps the chip.
 *   • On iOS Safari (no BIP event), we open a 3-step Share → Add to
 *     Home Screen instruction modal instead.
 *   • Auto-hides in:
 *       - Capacitor native apps (already installed)
 *       - `display-mode: standalone` (already installed)
 *       - Session-dismiss via the X button
 *       - `?source=pwa` (visitor arrived from a home-screen tap)
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Share, X, ArrowUpFromLine, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "pwa_install_chip_dismissed";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS reports as Mac with touch.
  if (/macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document) return true;
  return false;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) return true;
  if (window.navigator?.standalone === true) return true; // iOS Safari
  if (window.Capacitor?.isNativePlatform?.()) return true;
  return false;
}

export default function PwaInstallChip({ slug, creatorName }) {
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const promptRef = useRef(null);
  // v147.99.22 — When a visitor is already logged in (i.e. an end-user
  // who signed up while claiming a coupon / following this brand),
  // the "Save <brand>" install chip is the wrong CTA — they already
  // have an account. Replace it with a bright "Enter Linkola" pill
  // that takes them straight to /app so they can pick up their tickets
  // + orders. Anonymous visitors keep the original install prompt.
  // v147.99.25 — Customers land on /app/my-stuff (their tickets +
  // receipts + saved brands) since /app defaults to the Business
  // dashboard which is irrelevant for them.
  const { user } = useAuth();
  const navigate = useNavigate();
  const showEnterLinkola = !!user?.user_id;
  const enterLinkolaTarget = user?.view_mode === "customer" ? "/app/my-stuff" : "/app";

  // Inject <link rel="manifest"> + apple-touch-icon + theme-color for the
  // current bio. The browser uses the FIRST manifest declared in <head>,
  // so we RENAME the static /manifest.json link's `rel` attribute while
  // mounted (data-linkola-pwa-parked alone isn't enough — the browser
  // only cares about `rel`). iOS's "Add to Home Screen" ignores the
  // manifest icons entirely and reads <link rel="apple-touch-icon">
  // instead, so we hide those too via rel rename + node injection.
  // Both are restored on unmount so navigating back to the marketing
  // site keeps the generic Linkola branding.
  useEffect(() => {
    if (!slug || typeof document === "undefined") return undefined;
    if (isStandalone()) return undefined;

    let cancelled = false;

    // 1. Park the static Linkola manifest — rename its rel so the
    //    browser no longer treats it as a Web App Manifest.
    const staticManifest = document.head.querySelector('link[rel="manifest"]:not([data-linkola-pwa])');
    if (staticManifest) {
      staticManifest.setAttribute("data-linkola-pwa-parked-rel", "manifest");
      staticManifest.rel = "linkola-parked-manifest";
    }

    // 2. Inject the dynamic per-slug manifest.
    const linkEl = document.createElement("link");
    linkEl.rel = "manifest";
    linkEl.href = `/api/pwa/manifest/${encodeURIComponent(slug)}.webmanifest`;
    linkEl.setAttribute("data-linkola-pwa", "1");
    document.head.appendChild(linkEl);

    // 3. Park the static apple-touch-icons the same way. `style.display`
    //    has no effect on <link> elements — the only reliable neutraliser
    //    is renaming `rel`. Stash the original rel per node so we can
    //    restore it on unmount.
    const parkedAppleIcons = [];
    document.head.querySelectorAll('link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"]').forEach((el) => {
      if (el.hasAttribute("data-linkola-pwa")) return; // don't park our own
      parkedAppleIcons.push({ el, originalRel: el.rel });
      el.setAttribute("data-linkola-pwa-parked-rel", el.rel);
      el.rel = "linkola-parked-apple-touch-icon";
    });

    // 4. Fetch the brand icon URL from the manifest and inject a fresh
    //    apple-touch-icon. Guarded against StrictMode double-mount +
    //    unmount-during-fetch races so we don't leak duplicate nodes.
    let injectedAppleIcon = null;
    fetch(linkEl.href, { credentials: "omit", cache: "no-cache" })
      .then((r) => r.ok ? r.json() : null)
      .then((manifest) => {
        if (cancelled) return;
        if (!manifest || !manifest.icons || !manifest.icons.length) return;
        const icon512 = manifest.icons.find((i) => (i.sizes || "").includes("512"));
        const iconAny = icon512 || manifest.icons[0];
        const iconHref = iconAny?.src;
        if (!iconHref) return;
        // Belt-and-braces: dedupe if a prior mount already injected one.
        const existing = document.head.querySelector('link[rel="apple-touch-icon"][data-linkola-pwa="1"]');
        if (existing) { injectedAppleIcon = existing; return; }
        injectedAppleIcon = document.createElement("link");
        injectedAppleIcon.rel = "apple-touch-icon";
        injectedAppleIcon.setAttribute("sizes", "180x180");
        injectedAppleIcon.href = iconHref;
        injectedAppleIcon.setAttribute("data-linkola-pwa", "1");
        document.head.appendChild(injectedAppleIcon);
      })
      .catch(() => { /* silent — fall back to Linkola icon */ });

    return () => {
      cancelled = true;
      try { document.head.removeChild(linkEl); } catch { /* noop */ }
      if (staticManifest && staticManifest.getAttribute("data-linkola-pwa-parked-rel")) {
        staticManifest.rel = staticManifest.getAttribute("data-linkola-pwa-parked-rel");
        staticManifest.removeAttribute("data-linkola-pwa-parked-rel");
      }
      parkedAppleIcons.forEach(({ el, originalRel }) => {
        el.rel = originalRel;
        el.removeAttribute("data-linkola-pwa-parked-rel");
      });
      // Remove any injected apple-touch-icon (either the one we made or
      // one left over from a StrictMode double-mount).
      document.head.querySelectorAll('link[rel="apple-touch-icon"][data-linkola-pwa="1"]').forEach((el) => {
        try { document.head.removeChild(el); } catch { /* noop */ }
      });
      injectedAppleIcon = null;
    };
  }, [slug]);

  // Register the service worker — required for Chrome install prompt.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    if (isStandalone()) return;
    // Fire-and-forget; failure is silent (SW is a nice-to-have here).
    navigator.serviceWorker.register("/pwa-bio-sw.js", { scope: "/u/" }).catch(() => undefined);
  }, []);

  // Show/hide gating.
  useEffect(() => {
    if (!slug || typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;
    // Visitor arrived from the home-screen tap — no need to prompt again.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("source") === "pwa") return undefined;
    } catch { /* ignore */ }
    // Per-slug session dismiss — a visitor dismissing on one hub doesn't
    // silence the chip on every other creator they visit.
    try {
      if (sessionStorage.getItem(`${DISMISS_KEY}::${slug}`) === "1") return undefined;
    } catch { /* ignore */ }

    // v147.99.22 — Logged-in visitors get an "Enter Linkola" pill
    // that doesn't need OS install signals. Show it immediately.
    if (showEnterLinkola) {
      setVisible(true);
      return undefined;
    }

    // iOS: no beforeinstallprompt. Show the chip after a small delay so
    // it doesn't interrupt the initial paint.
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }

    // Android/Chrome: wait for the browser's install-eligible signal.
    const onBIP = (e) => {
      e.preventDefault();
      promptRef.current = e;
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [slug, showEnterLinkola]);

  if (!visible) return null;

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIosSheet(true);
      return;
    }
    const bip = promptRef.current;
    if (!bip) return;
    try {
      await bip.prompt();
      const choice = await bip.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      }
    } catch { /* user cancelled */ }
    promptRef.current = null;
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(`${DISMISS_KEY}::${slug}`, "1"); } catch { /* noop */ }
    setVisible(false);
  };

  const label = creatorName ? `Save ${creatorName}` : "Add to Home Screen";

  // v147.99.22 — Logged-in visitors get a distinct "Enter Linkola"
  // pill: brighter tint (sun/coral) + arrow icon, tapping routes to
  // /app. Keeps the dismiss X so they can silence it per-slug too.
  if (showEnterLinkola) {
    return (
      <div
        className="fixed z-[65] bottom-4 right-3 sm:right-4 flex items-center"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
        data-testid="pwa-install-chip"
      >
        <button
          type="button"
          onClick={() => navigate(enterLinkolaTarget)}
          className="inline-flex items-center gap-2 border-2 border-ink bg-sun text-ink pl-3 pr-2 py-2 rounded-full shadow-brutalSm hover:translate-y-[1px] transition-transform"
          data-testid="pwa-enter-linkola-btn"
        >
          <span className="w-6 h-6 rounded-full bg-coral border-2 border-ink flex items-center justify-center flex-shrink-0">
            <ArrowRight className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </span>
          <span className="text-[11px] font-black uppercase tracking-tight">
            Enter Linkola
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="ml-1 p-0.5 text-ink/50 hover:text-ink"
            data-testid="pwa-enter-linkola-dismiss"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed z-[65] bottom-4 right-3 sm:right-4 flex items-center"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
        data-testid="pwa-install-chip"
      >
        <button
          type="button"
          onClick={handleInstall}
          className="inline-flex items-center gap-2 border-2 border-ink bg-white text-ink pl-3 pr-2 py-2 rounded-full shadow-brutalSm hover:translate-y-[1px] transition-transform"
          data-testid="pwa-install-chip-btn"
        >
          <span className="w-6 h-6 rounded-full bg-coral border-2 border-ink flex items-center justify-center flex-shrink-0">
            <Plus className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </span>
          <span className="text-[11px] font-black uppercase tracking-tight max-w-[140px] truncate">
            {label}
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            className="ml-1 p-0.5 text-ink/50 hover:text-ink"
            data-testid="pwa-install-chip-dismiss"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </button>
      </div>

      {/* iOS instructions sheet (Safari's Share → Add to Home Screen). */}
      {showIosSheet && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowIosSheet(false)}
          data-testid="pwa-ios-sheet"
        >
          <div
            className="bg-white border-4 border-ink shadow-brutal w-full sm:max-w-sm p-5 rounded-t-lg sm:rounded-none space-y-4"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 0))" }}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display font-black text-lg tracking-tight">
                  Save {creatorName || "this page"}
                </h3>
                <p className="text-xs text-ink/60 mt-0.5">
                  Add it to your home screen in 3 taps.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowIosSheet(false)}
                className="p-1 text-ink/60 hover:text-ink"
                aria-label="Close"
                data-testid="pwa-ios-sheet-close"
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <ol className="space-y-3">
              <li className="flex items-center gap-3 text-sm">
                <span className="w-7 h-7 border-2 border-ink bg-sun flex items-center justify-center font-black text-xs flex-shrink-0">1</span>
                <span className="flex-1">
                  Tap the <Share className="w-4 h-4 inline align-text-bottom mx-0.5" strokeWidth={2.5} /> Share icon in Safari&apos;s toolbar.
                </span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <span className="w-7 h-7 border-2 border-ink bg-mint flex items-center justify-center font-black text-xs flex-shrink-0">2</span>
                <span className="flex-1">
                  Scroll and tap <ArrowUpFromLine className="w-4 h-4 inline align-text-bottom mx-0.5" strokeWidth={2.5} /> <b>Add to Home Screen</b>.
                </span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <span className="w-7 h-7 border-2 border-ink bg-coral text-white flex items-center justify-center font-black text-xs flex-shrink-0">3</span>
                <span className="flex-1">
                  Tap <b>Add</b> in the top-right — done!
                </span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIosSheet(false)}
              className="w-full border-2 border-ink bg-ink text-white px-3 py-2.5 font-black text-sm shadow-brutalSm"
              data-testid="pwa-ios-sheet-got-it"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
